/**
 * Panel de inicio del backoffice (ADM-07).
 *
 * Responde en una pantalla las cuatro preguntas del dueno: cuanto se vendio,
 * cuanto entro por WhatsApp, que le falta al catalogo y si algo quedo sin
 * registrar en Random. Lo ultimo va primero cuando pasa: un pedido pagado que
 * no llego a Random es plata que se pierde si nadie lo mira.
 */
import { Link } from 'react-router-dom';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { GraficoBarras } from '../../componentes/administracion/GraficoBarras';
import { LineaTiempo } from '../../componentes/administracion/LineaTiempo';
import { ListaBarras } from '../../componentes/administracion/ListaBarras';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { TarjetaKpi } from '../../componentes/administracion/TarjetaKpi';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerPanel } from '../../servicios/administracion.servicio';
import { numero, pesos } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';

export function Panel() {
  const { estado, datos, error } = usePeticion((senal) => obtenerPanel(senal), []);

  return (
    <>
      <Seo
        titulo="Panel"
        descripcion="Resumen de la operación de la tienda."
        ruta={rutas.administracion.panel()}
        sinIndexar
      />

      <BarraAdmin titulo="Panel" bajada="Resumen de la operación" />

      <div className="admin-cuerpo">
        {estado === 'cargando' ? <Cargando que="el panel" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null ? (
          <>
            <ul className="tarjetas-kpi" aria-label="Cifras del periodo">
              <TarjetaKpi
                icono="grafico"
                etiqueta="Vendido en línea"
                valor={pesos(datos.metricas.montoVendido)}
                detalle={`${numero(datos.metricas.ventasPagadas)} pedidos pagados`}
              />
              <TarjetaKpi
                tono="wa"
                icono="wa"
                etiqueta="Contactos por WhatsApp"
                valor={numero(datos.metricas.contactosWhatsapp)}
                detalle="Conversaciones abiertas desde el sitio"
              />
              <TarjetaKpi
                tono="ambar"
                icono="lupaVacia"
                etiqueta="Búsquedas sin resultado"
                valor={numero(datos.metricas.busquedasSinResultado)}
                detalle="Oportunidades de catálogo"
              />
              <TarjetaKpi
                tono={datos.metricas.pedidosSinSincronizar > 0 ? 'ambar' : 'verde'}
                icono="sincro"
                etiqueta="Pedidos sin registrar en Random"
                valor={numero(datos.metricas.pedidosSinSincronizar)}
                detalle={
                  datos.metricas.pedidosSinSincronizar > 0
                    ? 'Hay que regularizarlos'
                    : 'Todo al día'
                }
              />
            </ul>

            {/*
              Las dos columnas se reparten los cinco paneles por peso, no por
              tema: el grafico y la bitacora de Random son los mas largos (uno
              crece con los ultimos 14 dias, la otra con las ultimas corridas),
              asi que van cada uno con uno de los paneles cortos y no los tres
              juntos del mismo lado. Con eso junto, la columna corta se quedaba
              vacia bajo el grafico mientras la otra seguia mucho mas abajo.
            */}
            <div className="panel-doble">
              <div>
                <PanelAdmin
                  titulo="Venta diaria"
                  bajada={`Últimos ${datos.ventasDiarias.length} días`}
                >
                  <GraficoBarras
                    valores={datos.ventasDiarias}
                    etiqueta="Venta diaria de los últimos días"
                  />
                </PanelAdmin>

                <PanelAdmin
                  titulo="Integración Random"
                  extra={
                    <Link className="btn btn-xs btn-contorno" to={rutas.administracion.random()}>
                      Ver detalle
                      <Icono nombre="flecha" />
                    </Link>
                  }
                >
                  <LineaTiempo sincronizaciones={datos.ultimasSincronizaciones} />
                </PanelAdmin>
              </div>

              <div>
                {/*
                  El desglose es lo que convierte el total en algo accionable: no
                  es lo mismo que las consultas salgan de las fichas —donde el
                  comprador ya sabe que quiere— que del boton flotante, que suele
                  ser alguien que no encontro lo que buscaba. Va en barras y no
                  en una lista de numeros: son nueve origenes y lo que importa es
                  cual concentra la mayoria, no leer cada cifra suelta.
                */}
                <PanelAdmin
                  titulo="De dónde salen los contactos"
                  bajada="Cada clic a un botón de WhatsApp, por parte del sitio"
                >
                  {datos.contactosWhatsapp.length > 0 ? (
                    <ListaBarras
                      items={datos.contactosWhatsapp.map((contacto) => ({
                        etiqueta: contacto.nombre,
                        valor: contacto.cantidad,
                      }))}
                    />
                  ) : (
                    <p className="panel-vacio">Todavía nadie escribió por WhatsApp.</p>
                  )}
                </PanelAdmin>

                <PanelAdmin titulo="Estado del catálogo">
                  <ul className="lista-cifras">
                    <li>
                      <span>Productos publicados</span>
                      <span className="etq etq-neutra">
                        {numero(datos.estadoCatalogo.publicados)}
                      </span>
                    </li>
                    <li>
                      <span>Ocultos</span>
                      <span className="etq etq-neutra">{numero(datos.estadoCatalogo.ocultos)}</span>
                    </li>
                    {/*
                      Publicados en el panel pero sin precio en Random: la
                      tienda no los muestra. Solo aparece si hay alguno, porque
                      en cero no le dice nada a nadie.
                    */}
                    {datos.estadoCatalogo.publicadosSinPrecio > 0 ? (
                      <li>
                        <span>Publicados sin precio (no se ven)</span>
                        <span className="etq etq-alerta">
                          {numero(datos.estadoCatalogo.publicadosSinPrecio)}
                        </span>
                      </li>
                    ) : null}
                    <li>
                      <span>Sin stock</span>
                      <span
                        className={
                          datos.estadoCatalogo.sinStock > 0 ? 'etq etq-critico' : 'etq etq-neutra'
                        }
                      >
                        {numero(datos.estadoCatalogo.sinStock)}
                      </span>
                    </li>
                    <li>
                      <span>Stock crítico</span>
                      <span
                        className={
                          datos.estadoCatalogo.stockCritico > 0
                            ? 'etq etq-alerta'
                            : 'etq etq-neutra'
                        }
                      >
                        {numero(datos.estadoCatalogo.stockCritico)}
                      </span>
                    </li>
                  </ul>
                </PanelAdmin>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
